import React, { useRef, useState, useEffect } from 'react';
import useTiming from '../../hooks/useTiming';
import { useFormatDate } from "../../hooks/dates";
import VimeoPlayer from '../Video/VimeoPlayer';
import PlyrPlayer from '../Video/PlyrPlayer';
import useFetchPlayerData from '../../hooks/useFetchPlayerData';
import spaceState from "../../state/spaceState";
import {useRecoilValue} from "recoil";

const Indicator = ({data}) => {

    const timing = useTiming();
    const { formatTime } = useFormatDate()

    const opensAt = formatTime(timing.opens_at);
    const startsAt = formatTime(timing.starts_at);
    const endsAt = formatTime(timing.ends_at);
    const closesAt = formatTime(timing.closes_at);

    switch(data.title) {
        case "pre-show":
            return (
                <div className={`track-indicator-wrapper ${data.playing ? 'playing':''}`}>
                    <div className="track-indicator-content">
                        <p className={`${data.playing ? 'text-xs':'text-xl'} opacity-50 uppercase tracking-wider font-semibold`}>Open</p>
                        <p className={`${data.playing ? 'text-base':'text-3xl'} font-semibold text-6lg`}>{opensAt}</p>
                    </div>
                </div>
            );
        case "live":
            return (
                <>
                    <div className={`track-indicator-wrapper text-left-center ${data.playing ? 'playing':''}`}>
                        <div className="track-indicator-content">
                            <p className={`${data.playing ? 'text-xs':'text-xl'} opacity-50 uppercase tracking-wider font-semibold`}>Start</p>
                            <p className={`${data.playing ? 'text-base':'text-3xl'} font-semibold text-6lg`}>{startsAt}</p>

                        </div>
                    </div>
                    <div className={`track-indicator-wrapper right text-right-center ${data.playing ? 'playing':''}`}>
                        <div className="track-indicator-content">
                            <p className={`${data.playing ? 'text-xs':'text-xl'} opacity-50 uppercase tracking-wider font-semibold`}>End</p>
                            <p className={`${data.playing ? 'text-base':'text-3xl'} font-semibold text-6lg`}>{endsAt}</p>
                        </div>
                    </div>
                </>
            );
        case "post-show":
            return (
                <div className={`track-indicator-wrapper right ${data.playing ? 'playing':''}`}>
                    <div className="track-indicator-content">
                        <p className={`${data.playing ? 'text-xs':'text-xl'} opacity-50 uppercase tracking-wider font-semibold`}>Close</p>
                        <p className={`${data.playing ? 'text-base':'text-3xl'} font-semibold text-6lg`}>{closesAt}</p>
                    </div>
                </div>
            );
        default:
            return null;
    }
}

const Track = ({ largeVideo }) => {

    const {data, isLoading, isError} = useFetchPlayerData();
    const [player, setPlayer] = useState([]);
    const {isStarted, isEnded} = useRecoilValue(spaceState);

    const timing = useTiming();
    const { formatDuration } = useFormatDate()

    const durationPreshow = formatDuration(timing.opens_at, timing.starts_at);
    const durationLiveshow = formatDuration(timing.starts_at, timing.ends_at);
    const durationPostshow = formatDuration(timing.ends_at, timing.closes_at);

    useEffect(() => {
        if(!data?.hash) {
          return;
        }

        setPlayer([
            {
                title: 'pre-show',
                playing: !isStarted && !isEnded,
                duration: durationPreshow,
                setting: {
                    src: data?.pre?.src || data?.src,
                    provider: data?.pre?.provider,
                    loop: true,
                    sync: false,
                }
            },
            {
                title: 'live',
                playing: isStarted && !isEnded,
                duration: durationLiveshow,
                setting: {
                    src: data?.src,
                    provider: data?.provider,
                    loop: false,
                    sync: data?.sync,
                }
            },
            {
                title: 'post-show',
                playing: isEnded,
                duration: durationPostshow,
                setting: {
                    src: data?.post?.src || data?.src,
                    provider: data?.post?.provider,
                    loop: true,
                    sync: false,
                }
            },
        ]);
    }, [data?.hash]);

    const videoRef = useRef(null)

    return (
        <div
            ref={videoRef}
            className={`video ${largeVideo ? 'video-large' : ''} bg-panel`}
            style={{ top: 0, backgroundColor: 'transparent' }}
        >
            <div className="booth-video">
                <div className="flex flex-row pt-16">
                {player.map((data, index)=>(
                    <div className="w-4/12 text-center text-white relative">
                        <p className={`${data.playing ? '':'translate-y-5'} transform uppercase text-teal-400 text-xs tracking-wider font-semibold`}>{data.title}</p>
                    </div>
                ))}
                </div>
                <div className="flex flex-row gap-1 items-center relative">
                    <div className="border-2 border-teal-400 absolute top-0 right-0 left-0 bottom-0 transform scale-y-50 scale-x-84 rounded-lg"></div>
                    {player.map((data, index)=>(
                        <div className="w-4/12 text-center text-white relative">
                            <div className={`${data.playing ? 'scale-90':'scale-50'} transform relative`}>

                                <Indicator data={data}/>

                                {data.setting.src ?
                                    <div className={`${data.playing ? 'border-2 border-red-500':''} relative overflow-hidden rounded-lg`}>
                                        {/* Plyr and Vimeo Player */}
                                        {/* <PlyrPlayer className={`video-container-plyr bg-black`} provider="vimeo" src="687292066" /> */}
                                        <VimeoPlayer className="video-container bg-black" {...data.setting} />
                                    </div>
                                :
                                    <div className="aspect-w-16 aspect-h-9 relative flex overflow-hidden rounded-lg">
                                        <div className="w-full h-full bg-gray-600 flex items-center justify-center rounded">
                                            <i className="fas fa-video-slash text-4xl text-white"></i>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex flex-row my-3">
                    {player.map((data,index)=>(
                        <div className="w-4/12 text-center text-white relative">
                            {data.setting.src ?
                                <>
                                    <p className="font-semibold text-sm">{data.duration}</p>
                                    <p className="flex items-center justify-center space-x-1 text-sm">
                                        {(data.setting.provider === "vimeo-native" || data.setting.provider === "vimeo") && <i className="fab fa-vimeo fa-fw"></i>}
                                        {data.setting.provider === "youtube" && <i className="fab fa-youtube fa-fw"></i>}
                                        {data.setting.provider === "mp4" && <i className="fas fa-file-video fa-fw"></i>}
                                        <span>{data.setting.src}</span>
                                    </p>
                                    {data.setting.sync || data.setting.loop &&
                                        <p className="inline-block text-2xs mt-1 rounded-full px-3 py-1 bg-teal-400 bg-opacity-40 uppercase tracking-wide">
                                            {data.setting.sync ? 'sync':''} {data.setting.loop ? 'looped':''}
                                        </p>
                                    }
                                </>
                            :
                                <p className="font-semibold text-sm">No video data</p>
                            }
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Track
